import type {Location} from "react-router";
import {generatePath} from "react-router";
import type {AnyRouteMatch} from "./catRoutes";
import type {RouteKey} from "./routes";
import {routes} from "./routes";
import {environment} from "../env";
import type {AppState} from "../stores/RootAppStore";
import {DEFAULT_APP_STATE} from "../stores/RootAppStore";

export function tryGeneratePath(params: object, route: false | RouteKey | Array<RouteKey>, currentId: number | undefined, log = false): string | null {
    if (!route) return null

    const _routes = Array.isArray(route) ? route : [route];
    for (const _route of _routes) {
        const route = routes[_route];
        if (route.includes(':id') && !Object.hasOwn(params, 'id')) {
            // Expected :id but not provided in params

            // Try to get id from current databag if possible
            if (currentId) {
                params['id'] = String(currentId);
            } else {
                // Cannot generate path without id
                // Reduce console noise by only logging in dev mode
                // TODO: Ensure the home button always works
                if (!environment.production && log) {
                    console.warn(`Cannot generate path for route ${_route} because :id parameter is missing and no current databag id is available.`);
                }
                continue
            }
        }
        try {
            return generatePath(route, params);
        } catch (e) {
            if (log) console.error(`Error generating path for route ${_route} with params ${JSON.stringify(params)}:`, e);
        }
    }
    return null;
}

export function lookupHomePath(location: Location, state?: AppState, logo?: boolean): string {
    // In case that features that are accessible from home are enabled, regardless of package home should be returned
    if (logo) {
        return routes.home;
    }
    if (environment.package === 'cleanup') {
        // Placeholder for future conditions
    }
    if (environment.package === 'sales_demo') {
        // Placeholder for future conditions
    }
    if (state?.dataType === 'no_data') {
        return routes.home;
    }
    return routes.homepage_dashboard;
}

/**
 * Generates a path to the home page of the current environment, if a certain ID is present in the URL
 * @param location
 * @param params to override the params
 * @param state
 */
export function generateHomePath<M extends AnyRouteMatch>(location: Location<M>, params?: M, state?: AppState) {
    // eslint-disable-next-line no-prototype-builtins
    const path = lookupHomePath(location, state, params && params.hasOwnProperty('logo'));
    return generatePath(path, params);
}

export function generateDashboardPath<M extends AnyRouteMatch>(location: Location<M>, params?: M, state?: AppState) {
    const s: AppState = state ? {...state} : {...DEFAULT_APP_STATE};
    if (s.dataType === 'no_data') {
        delete s.dataType
    }
    const path = lookupHomePath(location, s);
    return generatePath(path, params);
}

export function generateGotoTaxonomyBuilderPath(databagId: number, taxonomyId?: number) {
    if (environment.package === 'cleanup') {
        // Placeholder for future conditions
    }
    if (environment.package === 'sales_demo') {
        // Placeholder for future conditions
    }
    // Fallback
    if (taxonomyId) {
        return generatePath(routes.cleanup_taxonomy_builder_id, {id: databagId, taxonomyId: taxonomyId});
    }
    return generatePath(routes.cleanup_taxonomy_builder, {id: databagId});
}

export function generateGotoTaxonomySuggestor(databagId: number, taxonomyId: number) {
    if (environment.package === 'sales_demo') {
        return generatePath(routes.demo_merge_taxonomy_suggester, {id: databagId, taxonomyId: taxonomyId});
    }
    throw new Error('Not implemented');
}

export function generateGotoDashboardPath<M extends AnyRouteMatch>(location: Location<M>, params?: M, state?: AppState) {
    if (environment.package === 'cleanup') {
        // Placeholder for future conditions
    }
    if (environment.package === 'sales_demo') {
        // Placeholder for future conditions
    }
    return generateHomePath(location, params);
}

/**
 * @see https://reactrouter.com/en/main/start/concepts#locations
 */
export function locationToURL(location: Location): string {
    return location.pathname + location.search + location.hash;
}
